import { createSelector } from "reselect"
import { get, find, chain, map } from "lodash"
import { getCurrent, getExposureData } from "./exposure"
import { getFactorCategory, getFactorLabel, getIsAggregatedFactor, makeNonZeroKeysSelectorFromData, computeAvailableSelectViewFactors } from "helpers/factors"
import { getCategories as getModelCategories, getSelectedViews, getSelectViewAggregatedFactorsMetadata, getDisplayAddView as getModelDisplayAddView, getFactorsMetadata } from "selectors/factors"
import { getFavoriteFactors, getMustAggregateFactors, getStyleExposureAggregation } from "modules/portfolio-root/selectors/portfolios"
import { getContentSets } from "selectors/contentSets"
import { COLORS } from "helpers/analyzeConstants"
import { computeDisplayType, computeDisplayTypeName, getAllCategoryItems as getAllCategoryItemsFn, getOrderedCategoryItems as getOrderedCategoryItemsFn } from "helpers/analyze"
import { getModelFactorsClassification } from "selectors/modelMetadata"

export const getDisabled = state => get(state, "modules.analyzeHistoricalExposure.disabled")
const getRawDisplayType = state => get(state, "modules.analyzeHistoricalExposure.displayType")
export const getContentSetId = (_, props) => get(props, "params.contentSetId", get(props, "contentSetId"))

export const getCategories = createSelector(
  [getModelCategories, getContentSets, getContentSetId],
  (categories, contentSets, contentSetId) => {
    if (contentSetId) {
      const contentSet = find(contentSets, ({ id }) => id === contentSetId)
      if (contentSet) {
        return map(contentSet.categories, ({ id, name }, index) => ({
          id, label: name, color: COLORS[index],
        }))
      }
      return null
    }

    return categories
  },
)

export const getFactors = createSelector(
  [getFactorsMetadata, getContentSets, getContentSetId],
  (factors, contentSets, contentSetId) => {
    if (contentSetId) {
      const contentSet = find(contentSets, ({ id }) => id === contentSetId)
      if (contentSet) {
        return chain(contentSet.factors)
          .keyBy("id")
          .mapValues(({ name, category }) => ({
            category, label: name,
          }))
          .value()
      }
      return null
    }

    return factors
  },
)

export const getDisplayType = createSelector(
  [getRawDisplayType, getCategories],
  computeDisplayType,
)

export const getDisplayAggregationToggle = createSelector(
  [getDisplayType, getMustAggregateFactors],
  (displayType, mustAggregateFactors) => mustAggregateFactors && displayType === "style",
)

export const getDisplayTypeName = createSelector(
  [getRawDisplayType, getCategories],
  computeDisplayTypeName,
)

export const getAllItems = createSelector(
  [getFactors, getCurrent, getDisabled, getFavoriteFactors],
  (factorsMetadata, lastDatum, disabled, favoriteFactors) => map(factorsMetadata, (_, id) => ({
    id,
    label: getFactorLabel(factorsMetadata, id),
    category: getFactorCategory(factorsMetadata, id),
    favorite: favoriteFactors[id],
    lastValue: get(lastDatum, ["factors", id], 0),
    field: `factors.${id}`,
    disabled: !!disabled[id],
    isAggregated: getIsAggregatedFactor(factorsMetadata, id),
    selectedView: factorsMetadata[id].selectedView,
  })),
)

const getNonZerosKeys = makeNonZeroKeysSelectorFromData({
  getter: value => get(value, "factors"),
  isAboveZeroTreshold: (value) => Math.abs(value) !== 0,
  dataSelector: getExposureData,
  favoriteSelector: getFavoriteFactors,
  factorValueField: "net",
})

export const getAvailableSelectViewFactors = createSelector(
  [getSelectViewAggregatedFactorsMetadata, getModelFactorsClassification, getSelectedViews, getNonZerosKeys],
  computeAvailableSelectViewFactors,
)

export const getAllCategoryItems = createSelector(
  [getAllItems, getDisplayType, getStyleExposureAggregation],
  getAllCategoryItemsFn,
)

export const getOrderedCategoryItems = createSelector(
  [getAllItems, getDisplayType, getStyleExposureAggregation],
  getOrderedCategoryItemsFn,
)

export const getDisplayAddView = createSelector(
  [getModelDisplayAddView, getContentSetId],
  (modelDisplayAddView, contentSetId) => modelDisplayAddView && !contentSetId,
)
